import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "wt_token";
const USER_KEY = "wt_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!token) { setUser(null); return; }
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    api.get("/auth/me")
      .then(r => { setUser(r.data); localStorage.setItem(USER_KEY, JSON.stringify(r.data)); })
      .catch(() => { logout(); });
    // eslint-disable-next-line
  }, [token]);

  // 401 interceptor
  useEffect(() => {
    const id = api.interceptors.response.use(
      r => r,
      err => {
        if (err.response?.status === 401 && token) logout();
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
    // eslint-disable-next-line
  }, [token]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setTokenState(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete api.defaults.headers.common["Authorization"];
    setTokenState(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const getToken = () => localStorage.getItem(TOKEN_KEY);
